import {
  DataGrid,
  GridToolbar,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import { Alert, Box } from '@mui/material';
import { GlassPanel } from '../GlassPanel';

interface DataTableProps<T extends { id: number | string }> {
  rows: T[];
  columns: GridColDef[];
  loading?: boolean;
  rowCount?: number;
  paginationMode?: 'server' | 'client';
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  errorMessage?: string;
  emptyMessage?: string;
  /**
   * Explicit fixed height. When omitted the table fills its flex parent —
   * host pages should wrap it in a flex column so the grid stretches.
   */
  height?: number | string;
  disableToolbar?: boolean;
}

/**
 * Standard table for resource list views. Wraps `<DataGrid>` with a paper
 * container, error/empty states, the built-in MUI X toolbar, and a tuned
 * default height so callers don't repeat boilerplate per page.
 */
export function DataTable<T extends { id: number | string }>({
  rows,
  columns,
  loading,
  rowCount,
  paginationMode = 'server',
  paginationModel,
  onPaginationModelChange,
  errorMessage,
  emptyMessage = 'No records found.',
  height,
  disableToolbar,
}: DataTableProps<T>) {
  const flexFill = height === undefined;
  return (
    <GlassPanel
      sx={{
        width: '100%',
        ...(flexFill && { flex: 1, minHeight: 320, display: 'flex', flexDirection: 'column' }),
      }}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ m: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box
        sx={flexFill ? { flex: 1, minHeight: 0, width: '100%' } : { height, width: '100%' }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          // initialState (not the controlled `density` prop) so the toolbar
          // density toggle continues to work.
          initialState={{ density: 'standard' }}
          slots={{
            toolbar: disableToolbar ? undefined : GridToolbar,
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                {loading ? 'Loading…' : emptyMessage}
              </Box>
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 250 },
            },
          }}
          sx={{ border: 0 }}
        />
      </Box>
    </GlassPanel>
  );
}
